import { Link } from 'react-router-dom';
import { 
  Heart, Shield, Award, Users, Target, Eye, ArrowRight, 
  CheckCircle, Building, Calendar, Handshake, Globe,
  Stethoscope, Activity, MapPin, Sparkles, Scale, 
  Lightbulb, HeartHandshake, MessageSquare, Flame,
  UsersRound, Equal, TrendingUp, Zap, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function AboutBackground() {
  const milestones = [
    { year: '2003', title: 'Foundation', description: 'BAOBAB Hospital was established to serve the healthcare needs of the Mapinga community.' },
    { year: '2008', title: 'Expansion', description: 'Added new departments including Surgery and Maternity services.' },
    { year: '2015', title: 'Modernization', description: 'Upgraded facilities with modern medical equipment and technology.' },
    { year: '2020', title: 'Community Health', description: 'Launched extensive community health outreach programs across Pwani.' },
    { year: '2024', title: 'Excellence Award', description: 'Recognized for outstanding healthcare services in the region.' },
  ];

  const coreValues = [
    {
      icon: Award,
      title: 'Professionalism',
      description: 'Maintaining the highest standards of professional conduct in all aspects of healthcare delivery. Our staff demonstrate competence, dedication, and responsibility while adhering to established medical, ethical, and regulatory standards.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Upholding honesty, transparency, and ethical behaviour in all interactions with patients, colleagues, partners, and the community. Our decisions are guided by strong moral principles, ensuring trust and fairness.'
    },
    {
      icon: HeartHandshake,
      title: 'Respect',
      description: 'Recognizing and valuing the dignity, rights, beliefs, and diversity of every individual. We foster an environment where everyone is treated with courtesy, empathy, and understanding regardless of differences.'
    },
    {
      icon: Scale,
      title: 'Accountability',
      description: 'Promoting a culture of responsibility for actions, decisions, and outcomes. Staff perform duties with diligence, ensuring services are delivered safely and in accordance with professional standards.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Patients are at the center of everything we do. We deliver patient-centred care that prioritizes needs, comfort, and safety while providing responsive services and compassionate support.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Encouraging creativity, learning, and adoption of modern healthcare practices. We support integration of new technologies and evidence-based practices to continuously improve healthcare outcomes.'
    },
    {
      icon: Heart,
      title: 'Compassion',
      description: 'The foundation of quality healthcare. Our staff provide care with empathy, kindness, and understanding, creating a healing environment where every patient feels valued and cared for.'
    },
    {
      icon: MessageSquare,
      title: 'Transparency',
      description: 'Promoting openness and honesty in communication and decision-making. Information regarding healthcare services and operations is communicated clearly to patients, staff, and stakeholders.'
    },
    {
      icon: Flame,
      title: 'Passion',
      description: 'Driven by dedication and strong commitment to improving community health and well-being. Staff demonstrate enthusiasm and motivation in delivering healthcare services with excellence.'
    },
    {
      icon: UsersRound,
      title: 'Collaboration',
      description: 'Effective teamwork is essential in quality healthcare. We promote cooperation among professionals, departments, and community stakeholders for coordinated service delivery.'
    },
    {
      icon: Equal,
      title: 'Equality',
      description: 'Committed to fairness and non-discrimination. All individuals receive equal access to healthcare services and respect regardless of gender, ethnicity, religion, or socioeconomic status.'
    },
    {
      icon: TrendingUp,
      title: 'Personal Growth & Wellness',
      description: 'Valuing professional development and employee well-being. We encourage continuous learning and promote a healthy work environment supporting both personal and professional growth.'
    },
    {
      icon: Zap,
      title: 'Simplicity',
      description: 'Promoting efficiency and clarity in processes. We simplify procedures, reduce unnecessary bureaucracy, and ensure patients can easily access healthcare services conveniently.'
    }
  ];

  const commitments = [
    'Quality healthcare delivery',
    'Community welfare promotion',
    'Compassionate treatment',
    'Modern service delivery',
    'Patient-centered care',
    'Safe healing environment',
    'Professional medical standards',
    'Accessible healthcare services'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary py-20 md:py-28" data-testid="about-hero">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-accent font-semibold mb-3 tracking-wide uppercase text-sm">About Us</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Background
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed">
              Learn about our history, mission, and unwavering commitment to serving the healthcare needs of Mapinga, Bagamoyo, Pwani, and neighboring communities.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section-spacing" data-testid="about-intro">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1736289162890-78f1ff4f8bd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwbWVkaWNhbCUyMHRlYW0lMjBkb2N0b3JzJTIwbnVyc2VzJTIwc21pbGluZ3xlbnwwfHx8fDE3NzMzMzc0NzB8MA&ixlib=rb-4.1.0&q=85"
                alt="BAOBAB Hospital Team"
                className="rounded-2xl shadow-float w-full"
              />
            </div>
            <div>
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Institution</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                A Healthcare Institution Serving Our Community
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Baobab Hospital is a distinguished healthcare institution dedicated to serving Mapinga, Bagamoyo, Pwani, and nearby communities. Named after the iconic Baobab tree—a symbol of strength, resilience, and community in African culture—our hospital embodies these values in every aspect of our service delivery.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                We are committed to providing quality healthcare that prioritizes the well-being of every patient who walks through our doors. Our approach combines modern medical practices with compassionate care, ensuring that our community receives the best possible healthcare services.
              </p>
              <p className="text-slate-600 leading-relaxed">
                At Baobab Hospital, we believe that accessible healthcare is a fundamental right. Our dedicated team of healthcare professionals works tirelessly to deliver patient-centered medical care in a safe, healing, and professional environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section - PREMIUM STYLED */}
      <section className="section-spacing bg-gradient-to-br from-primary via-primary to-teal-dark" data-testid="about-vision-mission">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Purpose</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Vision & Mission
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Guiding principles that drive our commitment to exceptional healthcare
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Mission Card */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden group hover:bg-white/15 transition-all duration-500">
              <CardContent className="p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <span className="text-accent/80 text-sm font-medium uppercase tracking-wider">Our</span>
                    <h3 className="font-heading text-3xl font-bold text-white">Mission</h3>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-accent to-accent/30 rounded-full" />
                  <p className="text-white/90 leading-relaxed text-lg pl-6">
                    To provide quality, compassionate and accessible healthcare services to Mapinga community and neighboring Pwani areas, ensuring a positive and healing environment that prioritizes health, well-being and community welfare.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Vision Card */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden group hover:bg-white/15 transition-all duration-500">
              <CardContent className="p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <span className="text-accent/80 text-sm font-medium uppercase tracking-wider">Our</span>
                    <h3 className="font-heading text-3xl font-bold text-white">Vision</h3>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-accent to-accent/30 rounded-full" />
                  <p className="text-white/90 leading-relaxed text-lg pl-6">
                    To become a leading healthcare provider in the Mapinga and Pwani areas, known for delivering exceptional medical services, fostering a patient approach and contributing to the overall well being of the community we serve.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Decorative Elements */}
          <div className="flex justify-center mt-12 gap-2">
            <div className="w-2 h-2 bg-accent rounded-full" />
            <div className="w-2 h-2 bg-accent/60 rounded-full" />
            <div className="w-2 h-2 bg-accent/30 rounded-full" />
          </div>
        </div>
      </section>

      {/* Core Values Section - PREMIUM STYLED */}
      <section className="section-spacing bg-warm" data-testid="about-core-values">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-2xl mb-6">
              <Star className="w-8 h-8 text-accent" />
            </div>
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">What We Stand For</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Core Values of Baobab Hospital
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto text-lg">
              These fundamental principles guide our conduct, shape our culture, and define how we deliver healthcare to our community.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <Card 
                key={index} 
                className="bg-white border-0 shadow-card hover:shadow-float transition-all duration-300 group overflow-hidden"
                data-testid={`core-value-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-teal-dark rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md">
                      <value.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-primary text-lg mb-2 group-hover:text-accent transition-colors">
                        {value.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mt-4">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Values Summary Bar */}
          <div className="mt-16 bg-white rounded-2xl shadow-card p-8">
            <div className="flex flex-wrap justify-center gap-4">
              {['Quality', 'Compassion', 'Excellence', 'Trust', 'Innovation', 'Care'].map((word, index) => (
                <span 
                  key={index}
                  className="px-6 py-2 bg-gradient-to-r from-primary/5 to-accent/5 rounded-full text-primary font-semibold text-sm border border-primary/10"
                >
                  {word}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Commitments Section */}
      <section className="section-spacing" data-testid="about-commitments">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Commitments</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
                What We Promise
              </h2>
              <p className="text-slate-600">
                Baobab Hospital is built on a foundation of commitments that guide everything we do.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {commitments.map((commitment, index) => (
                <div key={index} className="bg-teal-light p-4 rounded-xl flex items-center gap-3 hover:bg-primary/10 transition-colors">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-primary font-medium text-sm">{commitment}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-spacing bg-primary" data-testid="about-timeline">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our History</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Our Journey</h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Key milestones in our history of serving the community.
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-white/20 hidden md:block" />
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-testid={`milestone-${index}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card className="bg-white/10 backdrop-blur-sm border-white/20 inline-block">
                      <CardContent className="p-6">
                        <span className="text-accent font-bold text-3xl">{milestone.year}</span>
                        <h3 className="font-heading font-semibold text-white text-xl mt-2">{milestone.title}</h3>
                        <p className="text-white/80 mt-2">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="w-5 h-5 bg-accent rounded-full relative z-10 hidden md:block ring-4 ring-white/20" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-spacing" data-testid="about-location">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Location</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                Serving Pwani and Beyond
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Located in Mapinga, Bagamoyo District, Pwani Region, Baobab Hospital serves as a vital healthcare hub for the surrounding communities. Our strategic location allows us to provide accessible healthcare services to residents throughout the coastal region of Tanzania.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-light rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary">Address</h4>
                    <p className="text-slate-600">Mapinga – Bagamoyo, Pwani<br />P.O. Box 35692</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-light rounded-lg flex items-center justify-center flex-shrink-0">
                    <Activity className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary">Services Coverage</h4>
                    <p className="text-slate-600">Mapinga, Bagamoyo, and neighboring communities in Pwani Region</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-float h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63405.77775258752!2d38.87194049999999!3d-6.449999999999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c3f00f2d3c91b%3A0xb7b8a7c8e6f3e9a1!2sBagamoyo%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BAOBAB Hospital Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-accent" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Experience Quality Healthcare
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of patients who trust Baobab Hospital for their healthcare needs. Our doors are always open for you and your family.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/book-appointment" data-testid="about-book">
              <Button className="bg-white text-accent hover:bg-slate-100 rounded-full px-8 py-4 font-semibold shadow-lg">
                Book an Appointment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-contact">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-accent rounded-full px-8 py-4 font-semibold">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
